import { useMemo } from 'react';
import type { DocItem } from 'src/@types/docs';
import type { TreeItem } from 'src/pages/dashboard/DocsPage/shared/DocsTreeView/types';
import { useDocItemsContext } from './useDocItemsContext';

export const useDocsFavoriteItems = (): TreeItem[] => {
  const docs = useDocItemsContext((s) => s.docs);
  const childrenMap = useDocItemsContext((s) => s.childrenMap);

  return useMemo(() => {
    const favorites = docs.filter((doc) => doc.starred);
    const seen = new Set<string>();

    const toTreeItem = (doc: DocItem, parentOverride?: string): TreeItem => ({
      id: doc._id,
      parent: parentOverride ?? (doc.parent || 'root'),
      text: doc.title,
      data: {
        hasChildren:
          childrenMap[doc._id]?.some((child) => !child.starred) ?? false,
        order: doc.notesDocument ? -1 : doc.order!,
        createdAt: doc.createdAt,
        starred: doc.starred,
        icon: doc.icon,
      },
    });

    const result: TreeItem[] = [];
    const collectDescendants = (parentId: string) => {
      for (const children of childrenMap[parentId] ?? []) {
        if (children.starred) continue;
        if (seen.has(children._id)) continue;

        seen.add(children._id);
        result.push(toTreeItem(children));
        collectDescendants(children._id);
      }
    };

    for (const fav of favorites) {
      seen.add(fav._id);
      result.push(toTreeItem(fav, 'root'));
      collectDescendants(fav._id);
    }

    return result;
  }, [docs, childrenMap]);
};
